import React, {useEffect, useState} from "react";
import EnterpriseComponent from "../../eoec/ec/EnterpriseComponent";
import {prepareBuffer} from "../../util/Buffer";
import {Avatar, Button, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {Black, Blue3, Grey17, Grey3, Grey5, Grey6} from "../../theme/styles";
import {makeEnterpriseComponent} from "../../util/utility";
import css_buttons from "../../components/Common/css/CCButton.module.scss";
import {AddCircle, Delete, WarningAmber} from "@mui/icons-material";
import PrivilegeConstraintEmployeeFormApplet from "../../eoec/applet/impl/Administration/PrivilegeConstraintEmployeeFormApplet";
import MemoryContext from "../../util/MemoryContext";
import EventBus from "../../util/EventBus";
import {SessionContext} from "../../util/SessionContext";
import {PrivilegeRole} from "../../util/usePrivilegeRoles";

// Per-privilege permission block on an object's Permissions tab. Ported from t5's Catalog copy;
// t1's older Administration/PermissionConfiguration is a different component and is left alone.
// Two panels:
//
//   Roles       -- who grants this privilege and HOW (a direct grant, or via a capability bundle),
//                  with the users holding each role. Read-only provenance, supplied by the caller
//                  from usePrivilegeRoles so every block on the page resolves in one request.
//   Constraints -- per-user DENY rows scoped to THIS record (ParentUSID), which are editable here.
//
// The roles list is realm-wide: it says who is configured to hold the privilege, not who may act on
// this particular record, since a share can replace role access for one record.
interface Props {
    name: string;
    parentUSID: string;
    sessionContext?: SessionContext;
    // Resolved once for the whole page by usePrivilegeRoles; undefined while still loading.
    roles?: PrivilegeRole[];
    // True when the lookup FAILED. Distinct from an empty roles list, which means nobody grants it.
    rolesFailed?: boolean;
    rolesLoaded?: boolean;
}

export default function PermissionConfiguration(props: Props) {
    const [privilegeEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("Privilege"));
    const [PrivilegeConstraintEnterpriseComponent] = useState<EnterpriseComponent>(() => makeEnterpriseComponent("PrivilegeConstraint"));
    const [privilegeBuffer, setPrivilegeBuffer] = React.useState<Map<string, any>>(new Map<string, any>());
    const [showAddConstraint, setShowAddConstraint] = React.useState(false);
    const [showDeleteConstraint, setShowDeleteConstraint] = React.useState(false);
    const [showAddConstraintMenu, setShowAddConstraintMenu] = React.useState<EventTarget & HTMLButtonElement | null>(null);

    // Distinguishes "still loading" from "there is no such privilege". Without it, both render as
    // nothing at all, and a Permissions tab that is blank because the PRIVILEGE rows were never
    // seeded looks identical to one that is still fetching -- which is exactly how an unapplied
    // migration presents as a broken page.
    const [privilegeLoaded, setPrivilegeLoaded] = React.useState(false);

    function enterpriseComponentLoadCompletedListener(ec: EnterpriseComponent) {
        let b: Map<string, any>[] = prepareBuffer(ec);
        setPrivilegeBuffer(b[0]);
        setPrivilegeLoaded(true);
    }

    function enterpriseComponentValueChanged(ec: EnterpriseComponent) {
        let b: Map<string, any>[] = prepareBuffer(ec);
        setPrivilegeBuffer(b[0]);
    }

    function enterpriseComponentInvalidateListener() {
        privilegeEnterpriseComponent.newQuery();
        privilegeEnterpriseComponent.setAttributeValue("Name", props.name);
        privilegeEnterpriseComponent.setAttributeValue("ParentUSID", props.parentUSID);
        privilegeEnterpriseComponent.executeQuery(1, 1000);
    }

    useEffect(() => {
        privilegeEnterpriseComponent.newQuery();
        privilegeEnterpriseComponent.setAttributeValue("Name", props.name);
        privilegeEnterpriseComponent.setAttributeValue("ParentUSID", props.parentUSID);
        privilegeEnterpriseComponent.executeQuery(1, 1000);

        privilegeEnterpriseComponent.addInvalidListener(enterpriseComponentInvalidateListener);
        privilegeEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);
        privilegeEnterpriseComponent.addValueChangedListener(enterpriseComponentValueChanged);

        EventBus.components.addListener("admin-privilege-constraint-form-applet-save", onCancelHandler);
        EventBus.components.addListener("admin-privilege-constraint-form-applet-cancel", onCancelHandler);

        return () => {
            EventBus.components.removeListener("admin-privilege-constraint-form-applet-save", onCancelHandler);
            EventBus.components.removeListener("admin-privilege-constraint-form-applet-cancel", onCancelHandler);
            privilegeEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener);
            privilegeEnterpriseComponent.removeValueChangedListener(enterpriseComponentValueChanged);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [privilegeEnterpriseComponent, props.name, props.parentUSID]);

    function stringAvatar(name: string) {
        return (name || "").split(" ").map(word => word[0]).join("").substring(0, 2);
    }

    function onCancelHandler() {
        setShowAddConstraint(false);
        setShowDeleteConstraint(false);
        privilegeEnterpriseComponent.invalidate();
    }

    function renderPrivilege() {
        // Guard on the privilege itself having loaded. This previously keyed on a "ListOfGroup" node
        // that no Privilege IDOC has ever carried, so the whole tab rendered nothing.
        if (!privilegeBuffer || privilegeBuffer.get("USID") === undefined) {
            // Still fetching: render nothing rather than flash "not configured" at every block.
            if (!privilegeLoaded) return null;

            // Loaded, and there is no PRIVILEGE row with this NAME. Say so, with the name, instead of
            // rendering nothing: a silently blank tab is indistinguishable from a broken one, and the
            // usual cause is an unapplied privilege seed rather than anything wrong with the record.
            return (
                <>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "#FFFFFF", marginTop: "30px", borderBottom: "1px solid #D8DDE4"}}>
                        <Typography style={{fontSize: 20, fontWeight: 500, color: "#000000"}}>{props.name}</Typography>
                    </div>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", backgroundColor: "#FFF8EC", border: "1px solid #D8DDE4", borderTop: "0px", gap: 8, padding: "2px 0"}}>
                        <WarningAmber sx={{fontSize: 18, color: "#C9820A", marginLeft: "10px"}}/>
                        <Typography style={{fontSize: 14, fontWeight: 500, color: "#8A5B00", margin: "12px 0"}}>
                            No privilege named &ldquo;{props.name}&rdquo; exists, so nothing can grant it. This is a
                            configuration gap, not a fact about this record &mdash; the privilege seed has not been applied.
                        </Typography>
                    </div>
                </>
            );
        }

        const roles = props.roles || [];
        const constraints = (privilegeBuffer.get("ListOfPrivilegeConstraint") && privilegeBuffer.get("ListOfPrivilegeConstraint")["PrivilegeConstraint"]) || [];

        return (
            <>
                {showAddConstraint ? <PrivilegeConstraintEmployeeFormApplet enterpriseComponent={PrivilegeConstraintEnterpriseComponent} defaultAction={"New"}/> : null}
                {showDeleteConstraint ? <PrivilegeConstraintEmployeeFormApplet enterpriseComponent={PrivilegeConstraintEnterpriseComponent} defaultAction={"Delete"}/> : null}
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "#FFFFFF", marginTop: "30px", borderBottom: "1px solid #D8DDE4"}}>
                    <Typography style={{fontSize: 20, fontWeight: 500, color: "#000000"}}>{privilegeBuffer.get("Name")}</Typography>
                </div>
                <div style={{display: "flex", flexDirection: "column", backgroundColor: Grey3, border: "1px solid #7B8291", borderBottom: "0px", borderTopLeftRadius: "10px", borderTopRightRadius: "10px", marginTop: "20px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "transparent", borderBottom: "1px solid #7B8291"}}>
                        <div style={{width: "260px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Role</Typography></div>
                        <div style={{width: "5px"}}><Typography style={{fontSize: 26, fontWeight: 500, color: Grey6, marginTop: "0px"}}>|</Typography></div>
                        <div style={{width: "230px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Granted via</Typography></div>
                        <div style={{width: "5px"}}><Typography style={{fontSize: 26, fontWeight: 500, color: Grey6, marginTop: "0px"}}>|</Typography></div>
                        <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Assigned users</Typography></div>
                    </div>
                    {/* Three distinct states. Collapsing the first two into one another is what let a
                        500 masquerade as "nobody has this" for an entire session. */}
                    {props.rolesFailed ? (
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", backgroundColor: "#FFF8EC", gap: 8, padding: "2px 0"}}>
                            <WarningAmber sx={{fontSize: 18, color: "#C9820A", marginLeft: "10px"}}/>
                            <Typography style={{fontSize: 14, fontWeight: 500, color: "#8A5B00", margin: "12px 0 12px 0"}}>
                                Roles could not be loaded &mdash; this is not the same as nobody holding this privilege. Retry, or check the Foundation service.
                            </Typography>
                        </div>
                    ) : props.rolesLoaded === false ? (
                        <div style={{display: "flex", flexDirection: "row", backgroundColor: "#FFFFFF"}}>
                            <Typography style={{fontSize: 14, fontWeight: 500, color: Grey5, margin: "12px 0 12px 10px"}}>Loading roles&hellip;</Typography>
                        </div>
                    ) : roles.length === 0 ? (
                        <div style={{display: "flex", flexDirection: "row", backgroundColor: "#FFFFFF"}}>
                            <Typography style={{fontSize: 14, fontWeight: 500, color: Grey5, margin: "12px 0 12px 10px"}}>No roles grant this privilege</Typography>
                        </div>
                    ) : null}
                    {roles.map((role: PrivilegeRole, index: number) => (
                        <div key={index} style={{display: "flex", flexDirection: "row", justifyContent: "space-between", backgroundColor: "#FFFFFF", borderBottom: "1px solid #EDEFF3"}}>
                            <div style={{display: "flex", flexDirection: "row"}}>
                                <div style={{marginTop: "10px", marginBottom: "10px", width: "260px"}}>
                                    <Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px", textOverflow: "ellipsis", overflow: "hidden"}}>{role.RoleName}</Typography>
                                </div>
                                {/* Provenance: a capability grant is changed on the role's capability
                                    assignment, a direct grant on its privilege list -- different places,
                                    so the panel has to say which. */}
                                <div style={{marginTop: "10px", marginBottom: "10px", width: "235px"}}>
                                    <Typography style={{fontSize: 14, fontWeight: 400, color: role.GrantPath === "Capability" ? Blue3 : Grey6, margin: "10px", textOverflow: "ellipsis", overflow: "hidden"}}>
                                        {role.GrantPath === "Capability" ? (role.CapabilityName || "Capability") : "Direct grant"}
                                    </Typography>
                                </div>
                                <div style={{width: "300px", display: "flex", flexDirection: "column", marginTop: "5px"}}>
                                    {(role.AssignedUsers || []).length === 0 ? (
                                        <div style={{display: "flex", flexDirection: "row"}}>
                                            <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Grey5, margin: "15px 0 8px 10px"}}>No assigned users</Typography></div>
                                        </div>
                                    ) : null}
                                    {(role.AssignedUsers || []).map((user) => (
                                        <div key={user.USID} style={{display: "flex", flexDirection: "row"}}>
                                            <div style={{fontSize: 14, fontWeight: 500, color: Black, margin: "3px 0 0 10px"}}>
                                                <Avatar sx={{bgcolor: Blue3, width: 25, height: 25, fontSize: "12px"}}>{stringAvatar(user.Name)}</Avatar>
                                            </div>
                                            <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "5px 0 8px 10px", textWrap: "nowrap"}}>{user.Name}</Typography></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{display: "flex", flexDirection: "column", backgroundColor: Grey3, border: "1px solid #7B8291", borderBottom: "1px", marginTop: "0px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", backgroundColor: "transparent", borderBottom: "1px solid #7B8291"}}>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                            <div style={{width: "410px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Constraints</Typography></div>
                            <div style={{width: "5px"}}><Typography style={{fontSize: 26, fontWeight: 500, color: Grey6, marginTop: "0px"}}>|</Typography></div>
                            <div style={{width: "300px"}}><Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "10px"}}>Impact</Typography></div>
                        </div>
                        <div style={{padding: "6px 0 0 0"}}>
                            <Tooltip title={"Add Constraint"}>
                                <Button className={css_buttons.transparentStandardButton} style={{cursor: "pointer", height: "30px"}} size="small"
                                        onClick={(event) => setShowAddConstraintMenu(event.currentTarget)}
                                        startIcon={<AddCircle sx={{width: "25px", height: "25px", color: "#1498D8"}}/>}/>
                            </Tooltip>
                            {showAddConstraintMenu ? (
                                <Menu
                                    anchorEl={showAddConstraintMenu}
                                    anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                    transformOrigin={{vertical: -5, horizontal: "center"}}
                                    open={true}
                                    onClose={() => setShowAddConstraintMenu(null)}
                                    slotProps={{
                                        list: {disablePadding: true, style: {"minWidth": "200px"}},
                                        transition: {onExited: () => setShowAddConstraintMenu(null)}
                                    }}>
                                    <MenuItem key={"1"} onClick={() => {
                                        setShowAddConstraintMenu(null);
                                        setShowAddConstraint(true);
                                        // @ts-ignore
                                        MemoryContext.components["Privilege-PrivilegeUSID"] = privilegeBuffer.get("USID");
                                        // @ts-ignore
                                        MemoryContext.components["Privilege-ParentUSID"] = props.parentUSID;
                                        // @ts-ignore
                                        MemoryContext.components["Privilege-Type"] = "DENY_EMPLOYEE";
                                    }}>
                                        <Typography component="div" style={{fontSize: "15px", fontWeight: "400", color: Grey17}}>Employee Constraint</Typography>
                                    </MenuItem>
                                </Menu>
                            ) : null}
                        </div>
                    </div>
                    {constraints.filter((element: any) => element.Type === "DENY_EMPLOYEE" && element.ParentUSID === props.parentUSID).map((element: any, index: number) => (
                        <div key={index} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", backgroundColor: "#FFFFFF", borderBottom: "1px solid #D8DDE4", width: "100%"}}>
                            <div style={{display: "flex", flexDirection: "row", width: "100%"}}>
                                <div style={{marginTop: "5px", marginBottom: "5px", minWidth: "410px"}}>
                                    <Typography style={{fontSize: 14, fontWeight: 500, color: Black, margin: "5px 0 5px 10px", textOverflow: "ellipsis", overflow: "hidden"}}>{element.Name} ({element.Type})</Typography>
                                </div>
                                <div style={{display: "flex", flexDirection: "column", marginTop: "5px", width: "100%"}}>
                                    <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%"}}>
                                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                            <div style={{fontSize: 14, fontWeight: 500, color: Black, margin: "3px 0 0 10px"}}>
                                                <Avatar sx={{bgcolor: Blue3, width: 25, height: 25, fontSize: "12px"}}>{stringAvatar(element.AccountUserName)}</Avatar>
                                            </div>
                                            <div style={{width: "100%"}}><Typography style={{fontSize: 14, fontWeight: 400, color: Black, margin: "5px 0 8px 10px"}}><b>{element.AccountUserName}</b> will be denied the {privilegeBuffer.get("Name")} privilege for this record.</Typography></div>
                                        </div>
                                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "auto 0 auto 0"}}>
                                            <Tooltip title={"Delete Constraint"}>
                                                <Button className={css_buttons.transparentStandardButton} style={{cursor: "pointer", height: "30px"}} size="small" onClick={() => {
                                                    setShowDeleteConstraint(true);
                                                    // @ts-ignore
                                                    MemoryContext.components["Privilege-PrivilegeUSID"] = privilegeBuffer.get("USID");
                                                    // @ts-ignore
                                                    MemoryContext.components["Privilege-PrivilegeConstraintUSID"] = element.USID;
                                                }} startIcon={<Delete sx={{color: "#1498D8", width: "25px", height: "25px"}}/>}/>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    }

    return (<>{renderPrivilege()}</>);
}
